import { IonLabel } from '@ionic/react';
import classNames from 'classnames';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { Controller } from 'react-hook-form';

import { UiTypography } from '@/components/ui/Typography';

import './List.scss';

interface ListProps {
  TEST_ID?: string;
  className?: string;
  children: NonNullable<React.ReactNode>;
  direction?: 'row' | 'column';
  disableTopSpacing?: boolean;
}

export const OnboardingOptionsList: React.FC<ListProps> = ({
  TEST_ID,
  className,
  children,
  direction = 'column',
  disableTopSpacing = false,
}) => {
  return (
    <div
      className={classNames('onboarding-options-list', className, {
        'direction-row': direction === 'row',
        'no-top-margin': disableTopSpacing,
      })}
      data-testid={TEST_ID}
    >
      {children}
    </div>
  );
};

export const OnboardingOptionItem: React.FC<{
  TEST_ID?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: any;
  option: {
    id: string;
    title: string;
  };
  checked: boolean;
  name: string;
  theme?: 'dark-violet' | 'domination' | 'domination-transparent';
  onChange?: (value: string) => void;
  animate?: boolean;
  show?: boolean;
  expand?: boolean;
}> = ({
  TEST_ID,
  control,
  option,
  checked,
  theme,
  name,
  onChange,
  animate,
  show = true,
  expand,
}) => {
  const shouldReduceMotion = useReducedMotion();
  const canAnimate = animate && !shouldReduceMotion;

  if (canAnimate) {
    const scale = checked ? 1.03 : 0.97;

    return (
      <AnimatePresence>
        {show && (
          <motion.div
            variants={{
              hidden: { opacity: 0, y: '100%' },
              visible: { opacity: 1, y: 0, scale },
            }}
            initial="hidden"
            animate="visible"
            exit="hidden"
          >
            <UiTypography
              className={classNames('ion-activatable', {
                checked,
                'theme-dark-violet': theme === 'dark-violet',
              })}
              key={option.id}
              TEST_ID={TEST_ID}
              component="label"
              color="black"
              variant="body-medium"
            >
              <Controller
                control={control}
                name={name}
                render={({ field }) => (
                  <input
                    onChange={() => {
                      field.onChange(option.id);
                      onChange && onChange(option.id);
                    }}
                    checked={field.value === option.id}
                    type="checkbox"
                    value={option.id}
                  />
                )}
              />
              <IonLabel>{option.title}</IonLabel>
            </UiTypography>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  const scale = checked ? 1.03 : 1;

  return (
    <UiTypography
      className={classNames('ion-activatable', {
        checked,
        'theme-dark-violet': theme === 'dark-violet',
        'theme-domination': theme === 'domination',
        'theme-domination-transparent': theme === 'domination-transparent',
        expand,
      })}
      key={option.id}
      TEST_ID={TEST_ID}
      component="label"
      color="black"
      variant="body-medium"
      style={{
        scale,
      }}
    >
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <input
            onChange={() => {
              field.onChange(option.id);
              onChange && onChange(option.id);
            }}
            checked={field.value === option.id}
            type="checkbox"
            value={option.id}
          />
        )}
      />
      <IonLabel>{option.title}</IonLabel>
    </UiTypography>
  );
};
